import type {
  SupportCenterDemandType,
  SupportCenterDemandSelectionItem,
} from './supportCenterTriage'

/** Prontuário de atendimento psicológico (agenda) — persistido no Supabase. */

export const SUPPORT_CENTER_PSYCH_EMOTIONAL_STATES = [
  'Estabilizado',
  'Parcialmente estabilizado',
  'Em crise',
  'Sem avaliação possível',
] as const
export type SupportCenterPsychEmotionalState =
  (typeof SUPPORT_CENTER_PSYCH_EMOTIONAL_STATES)[number]

export const SUPPORT_CENTER_PSYCH_RISK_LEVELS = [
  'Sem risco',
  'Risco leve',
  'Risco moderado',
  'Risco alto',
] as const
export type SupportCenterPsychRiskLevel = (typeof SUPPORT_CENTER_PSYCH_RISK_LEVELS)[number]

export const SUPPORT_CENTER_PSYCH_REFERRALS = [
  'Nenhum',
  'Psiquiatria',
  'Assistência social',
  'UBS / Atenção básica',
  'CAPS',
  'Continuidade na assistência',
  'Outro',
] as const
export type SupportCenterPsychReferral = (typeof SUPPORT_CENTER_PSYCH_REFERRALS)[number]

export const SUPPORT_CENTER_PSYCH_FORECAST_KINDS = [
  'data_fim',
  'atendimentos_previstos',
] as const
export type SupportCenterPsychForecastKind =
  (typeof SUPPORT_CENTER_PSYCH_FORECAST_KINDS)[number]

export const SUPPORT_CENTER_PSYCH_FORECAST_KIND_LABELS: Record<
  SupportCenterPsychForecastKind,
  string
> = {
  data_fim: 'Data fim',
  atendimentos_previstos: 'Nº de atendimentos previstos',
}

export type SupportCenterAttendanceDemandType = Pick<
  SupportCenterDemandType,
  'id' | 'name' | 'requires_notes'
>
export type SupportCenterAttendanceSelectedDemandType = SupportCenterAttendanceDemandType & {
  notes: string | null
}
export type SupportCenterAttendanceDemandSelection = SupportCenterDemandSelectionItem[]

export type SupportCenterPsychAttendanceFormState = {
  demand_selections: SupportCenterAttendanceDemandSelection
  medications: string[]
  demand_notes: string
  intervention_notes: string
  patient_response: string
  emotional_state: SupportCenterPsychEmotionalState | ''
  risk_indicator: SupportCenterPsychRiskLevel | ''
  referral: SupportCenterPsychReferral | ''
  next_contact_date: string
  additional_notes: string
  forecast_kind: SupportCenterPsychForecastKind | ''
  forecast_end_date: string
  forecast_sessions_count: string
}

export type SupportCenterPsychAttendancePayload = {
  appointment_id: string
  demand_selections: SupportCenterAttendanceDemandSelection
  medications: string[]
  demand_notes: string
  intervention_notes: string
  patient_response: string | null
  emotional_state: SupportCenterPsychEmotionalState
  risk_indicator: SupportCenterPsychRiskLevel
  referral: SupportCenterPsychReferral | null
  next_contact_date: string | null
  additional_notes: string | null
  forecast_kind: SupportCenterPsychForecastKind | null
  forecast_end_date: string | null
  forecast_sessions_count: number | null
}

export const EMPTY_PSYCH_ATTENDANCE_FORM: SupportCenterPsychAttendanceFormState = {
  demand_selections: [],
  medications: [],
  demand_notes: '',
  intervention_notes: '',
  patient_response: '',
  emotional_state: '',
  risk_indicator: '',
  referral: '',
  next_contact_date: '',
  additional_notes: '',
  forecast_kind: '',
  forecast_end_date: '',
  forecast_sessions_count: '',
}

/** Campos do prontuário para leitura (detalhe / atendimento realizado). */
export const SUPPORT_CENTER_PSYCH_VIEW_FIELDS = [
  { key: 'demand_notes', label: 'Demanda trazida' },
  { key: 'intervention_notes', label: 'Intervenção realizada' },
  { key: 'patient_response', label: 'Resposta do paciente' },
  { key: 'emotional_state', label: 'Estado emocional' },
  { key: 'risk_indicator', label: 'Indicador de risco' },
  { key: 'referral', label: 'Encaminhamento' },
  { key: 'next_contact_date', label: 'Próximo contato' },
  { key: 'additional_notes', label: 'Observações adicionais' },
  { key: 'forecast_summary', label: 'Previsão' },
] as const

export type SupportCenterPsychAttendanceViewFieldKey =
  (typeof SUPPORT_CENTER_PSYCH_VIEW_FIELDS)[number]['key']

/** Prontuário psicológico carregado para exibição (detalhe / atendimento realizado). */
export type SupportCenterPsychAttendanceView = Record<
  SupportCenterPsychAttendanceViewFieldKey,
  string | null
>

/** Prontuário psicológico com metadado de envio (rascunho vs enviado). */
export type SupportCenterPsychAttendanceRecord = SupportCenterPsychAttendanceView & {
  demand_selections: SupportCenterAttendanceDemandSelection
  demand_types: SupportCenterAttendanceSelectedDemandType[]
  medications: string[]
  forecast_kind: SupportCenterPsychForecastKind | null
  forecast_end_date: string | null
  forecast_sessions_count: number | null
  submitted_at: string | null
}

/** Texto de atendimento (observações / orientação) com metadado de envio. */
export type SupportCenterAttendanceNotesRecord = {
  demand_selections: SupportCenterAttendanceDemandSelection
  demand_types: SupportCenterAttendanceSelectedDemandType[]
  notes: string | null
  submitted_at: string | null
}

export function formatPsychForecastSummary(input: {
  forecast_kind: string | null | undefined
  forecast_end_date: string | null | undefined
  forecast_sessions_count: number | null | undefined
}): string | null {
  const kind = String(input.forecast_kind ?? '').trim()
  if (kind === 'data_fim') {
    const date = String(input.forecast_end_date ?? '').trim().slice(0, 10)
    if (!date) return null
    const [y, m, d] = date.split('-')
    if (y && m && d) return `${d}/${m}/${y}`
    return date
  }
  if (kind === 'atendimentos_previstos') {
    const count = input.forecast_sessions_count
    if (count == null || !Number.isFinite(count) || count < 1) return null
    return count === 1 ? '1 atendimento previsto' : `${count} atendimentos previstos`
  }
  return null
}
